import { definitions } from '@luxuryescapes/contract-svc-agent'
import { getAllItemViews } from 'checkout/selectors/view/generic'
import { OFFER_TYPE_BED_BANK, OFFER_TYPE_TOUR_V2, OFFER_TYPE_CRUISE } from 'constants/offer'
import { sum, unique } from 'lib/array/arrayUtils'
import { reportClientError } from 'services/errorReportingService'

type ServerAgentAccount = definitions['userSimpleSchema']
type ServerAgencyDetails = definitions['userAgencyResponse']
type ServerAgentEmailStatus = definitions['userStatusResponse']

type CalculateCartCommissionBody = definitions['calculateCartCommissionBody']['cart']
type CalculateCartCommissionBodyItem = definitions['calculateCartCommissionBody']['cart']['items'][0]

interface BreakdownViewItem {
  price?: number
  total?: number
  memberTotal?: number
  memberPrice?: number
  surcharge?: number
  taxesAndFees?: number
  otherFees?: App.Checkout.OtherFees
}

const sumViewItemTotal = (item: BreakdownViewItem): number => (item.price ?? 0) + (item.surcharge ?? 0) + (item.otherFees && 'extraGuestSurcharge' in item.otherFees ? item.otherFees?.extraGuestSurcharge ?? 0 : 0)
const sumViewItemsTotal = (items: Array<BreakdownViewItem>): number => sum(items, (i) => sumViewItemTotal(i))

export function agentHubCheckEmailStatusMapper(response: ServerAgentEmailStatus): App.AgentHubEmailCheckStatus {
  return {
    status: response.status,
    agentId: response.agent_id,
  }
}

export function agentHubAccountMapper(response: ServerAgentAccount): App.AgentHubAccount {
  return {
    agentId: response.id,
    name: response.name,
    phone: response.phone,
    email: response.email,
    affiliation: response.affiliation,
    agencyId: response.agencyId || '',
    agencyName: response.agencyName || '',
    status: response.status as any,
  }
}

export function agentHubAgencyMapper(response: ServerAgencyDetails): App.AgentHubAgency {
  return {
    id: response.id,
    name: response.name,
    agencyIdentifier: response.businessCodeValue,
    agencyIdentifierType: response.businessCodeType,
    address: response.address,
    city: response.city,
    state: response.state,
    postcode: response.postcode,
    country: response.country,
    affiliation: response.affiliation,
    brand: response.brand,
  }
}

const getCommissionItems = (state: App.State): Array<CalculateCartCommissionBodyItem> => {
  const resultItems: Array<CalculateCartCommissionBodyItem> = []

  try {
    const { accommodationItemsView } = getAllItemViews(state).data

    if (accommodationItemsView.data.length > 0) {
      for (const item of accommodationItemsView.data) {
        if (item.offerType === OFFER_TYPE_BED_BANK) {
          const locations = [
            item?.offer?.property?.address.city || '',
            item?.offer?.property?.address.stateProvinceName || '',
            item.offer?.property?.address.countryName || '']
            .filter(Boolean)

          resultItems.push({
            type: item.offer?.productType || '',
            offerId: item.offerId,
            price: sumViewItemsTotal(item.itemViews),
            location: locations,
            vendor: '',
          })

          continue
        }

        if (item.offerType === OFFER_TYPE_TOUR_V2) {
          const tourV2StartLocation = () => {
            const variations = item.itemViews.map((x) => x.variation)
            if (variations.length === 0) return ['']
            const variation = variations[0]
            return [variation?.startLocation || '']
          }

          resultItems.push({
            type: item.offer?.productType || '',
            offerId: item.offerId,
            price: sumViewItemsTotal(item.itemViews),
            location: tourV2StartLocation(),
            vendor: '',
          })

          continue
        }

        if (item.offerType === OFFER_TYPE_CRUISE) {
          resultItems.push({
            type: item.offer?.productType || '',
            offerId: item.offerId,
            price: sumViewItemsTotal(item.itemViews),
            location: [item.location],
            vendor: '',
          })

          continue
        }

        const locations = unique((item?.offer?.locations || []).concat(item?.offer?.location || []))

        resultItems.push({
          offerId: item.offerId,
          type: item.offerType,
          price: sumViewItemsTotal(item.itemViews),
          location: locations,
          vendor: item.offer?.vendorName || '',
        })
      }
    }
  } catch (err) {
    reportClientError(err)
  }

  return resultItems
}

export const stateToCommissionOrder = (state: App.State): CalculateCartCommissionBody => {
  const items = getCommissionItems(state)

  return {
    items: getCommissionItems(state),
    total: sum(items, (i) => i.price || 0),
  }
}
